import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ExclamationTriangleIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/Button';
import toast from 'react-hot-toast';

interface EscalateModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentSeverity: string;
  currentPriority: string;
  onEscalate: (data: EscalateData) => Promise<void>;
}

export interface EscalateData {
  increaseSeverity: boolean;
  newSeverity?: string;
  setPriorityUrgent: boolean;
  reason: string;
}

const SEVERITY_LEVELS = ['MINIMAL', 'MODERATE', 'HIGH'];

export default function EscalateModal({
  isOpen,
  onClose,
  currentSeverity,
  currentPriority,
  onEscalate,
}: EscalateModalProps) {
  const [increaseSeverity, setIncreaseSeverity] = useState(true);
  const [setPriorityUrgent, setSetPriorityUrgent] = useState(true);
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate next severity level
  const currentSeverityIndex = SEVERITY_LEVELS.indexOf(currentSeverity);
  const canIncreaseSeverity = currentSeverityIndex < SEVERITY_LEVELS.length - 1;
  const nextSeverity = canIncreaseSeverity
    ? SEVERITY_LEVELS[currentSeverityIndex + 1]
    : currentSeverity;

  const canSetPriorityUrgent = currentPriority !== 'URGENT';

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setIncreaseSeverity(canIncreaseSeverity);
      setSetPriorityUrgent(canSetPriorityUrgent);
      setReason('');
    }
  }, [isOpen, canIncreaseSeverity, canSetPriorityUrgent]);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Please provide an escalation reason');
      return;
    }

    if (!increaseSeverity && !setPriorityUrgent) {
      toast.error('Please select at least one escalation action');
      return;
    }

    try {
      setIsSubmitting(true);
      await onEscalate({
        increaseSeverity: increaseSeverity && canIncreaseSeverity,
        newSeverity: increaseSeverity && canIncreaseSeverity ? nextSeverity : undefined,
        setPriorityUrgent: setPriorityUrgent && canSetPriorityUrgent,
        reason: reason.trim(),
      });
      toast.success('Incident escalated successfully');
      onClose();
    } catch (error) {
      console.error('Failed to escalate incident:', error);
      toast.error('Failed to escalate incident');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-start justify-center z-50 p-4 pt-16 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl w-full max-w-lg pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-secondary-200 dark:border-secondary-700">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-danger-100 dark:bg-danger-900/20 rounded-full flex items-center justify-center">
                    <ExclamationTriangleIcon className="w-5 h-5 text-danger-600 dark:text-danger-400" />
                  </div>
                  <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
                    Escalate Incident
                  </h2>
                </div>
                <button
                  onClick={onClose}
                  className="text-secondary-400 hover:text-secondary-600 dark:hover:text-secondary-300 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 space-y-4">
                {/* Info Box */}
                <div className="p-4 bg-warning-50 dark:bg-warning-900/10 border border-warning-200 dark:border-warning-800 rounded-lg">
                  <p className="text-sm text-warning-800 dark:text-warning-200">
                    <strong>Note:</strong> Escalating an incident will increase its severity and/or priority,
                    and add an escalation comment to the incident timeline.
                  </p>
                </div>

                {/* Escalation Options */}
                <div className="space-y-3">
                  {/* Increase Severity */}
                  <div
                    className={`p-4 rounded-lg border-2 transition-all ${
                      !canIncreaseSeverity
                        ? 'border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900/20 opacity-50'
                        : increaseSeverity
                        ? 'border-danger-500 bg-danger-50 dark:bg-danger-900/20'
                        : 'border-secondary-200 dark:border-secondary-700'
                    }`}
                  >
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={increaseSeverity}
                        onChange={(e) => setIncreaseSeverity(e.target.checked)}
                        disabled={!canIncreaseSeverity}
                        className="mt-1 w-4 h-4 text-danger-600 border-secondary-300 rounded focus:ring-danger-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-secondary-900 dark:text-white">
                            Increase Severity
                          </span>
                          {!canIncreaseSeverity && (
                            <span className="text-xs px-2 py-0.5 bg-secondary-200 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300 rounded">
                              Already at max
                            </span>
                          )}
                        </div>
                        {canIncreaseSeverity ? (
                          <div className="flex items-center space-x-2 mt-2 text-sm">
                            <span className="px-2 py-1 bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 rounded">
                              {currentSeverity}
                            </span>
                            <ArrowUpIcon className="w-4 h-4 text-danger-600 dark:text-danger-400" />
                            <span className="px-2 py-1 bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 rounded font-medium">
                              {nextSeverity}
                            </span>
                          </div>
                        ) : (
                          <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
                            Severity is already at HIGH
                          </p>
                        )}
                      </div>
                    </label>
                  </div>

                  {/* Set Priority to Urgent */}
                  <div
                    className={`p-4 rounded-lg border-2 transition-all ${
                      !canSetPriorityUrgent
                        ? 'border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-900/20 opacity-50'
                        : setPriorityUrgent
                        ? 'border-danger-500 bg-danger-50 dark:bg-danger-900/20'
                        : 'border-secondary-200 dark:border-secondary-700'
                    }`}
                  >
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={setPriorityUrgent}
                        onChange={(e) => setSetPriorityUrgent(e.target.checked)}
                        disabled={!canSetPriorityUrgent}
                        className="mt-1 w-4 h-4 text-danger-600 border-secondary-300 rounded focus:ring-danger-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-secondary-900 dark:text-white">
                            Set Priority to Urgent
                          </span>
                          {!canSetPriorityUrgent && (
                            <span className="text-xs px-2 py-0.5 bg-secondary-200 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-300 rounded">
                              Already urgent
                            </span>
                          )}
                        </div>
                        {canSetPriorityUrgent ? (
                          <div className="flex items-center space-x-2 mt-2 text-sm">
                            <span className="px-2 py-1 bg-secondary-100 dark:bg-secondary-700 text-secondary-700 dark:text-secondary-300 rounded">
                              {currentPriority}
                            </span>
                            <ArrowUpIcon className="w-4 h-4 text-danger-600 dark:text-danger-400" />
                            <span className="px-2 py-1 bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 rounded font-medium">
                              URGENT
                            </span>
                          </div>
                        ) : (
                          <p className="text-sm text-secondary-500 dark:text-secondary-400 mt-1">
                            Priority is already set to URGENT
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                </div>

                {/* Escalation Reason */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Escalation Reason <span className="text-danger-600">*</span>
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Explain why this incident needs to be escalated..."
                    rows={4}
                    className="w-full px-3 py-2 border border-secondary-300 dark:border-secondary-600 rounded-lg bg-white dark:bg-secondary-800 text-secondary-900 dark:text-white placeholder-secondary-400 focus:ring-2 focus:ring-danger-500 focus:border-danger-500 resize-none"
                    required
                  />
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                    This reason will be added as a comment to the incident timeline
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-secondary-200 dark:border-secondary-700">
                <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!reason.trim() || isSubmitting || (!increaseSeverity && !setPriorityUrgent)}
                  className="min-w-32 bg-danger-600 hover:bg-danger-700 text-white"
                >
                  {isSubmitting ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      <span>Escalating...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <ExclamationTriangleIcon className="w-4 h-4" />
                      <span>Escalate Incident</span>
                    </div>
                  )}
                </Button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
